import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Tabs,
  Tab,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Tooltip,
  useTheme,
  styled,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper as MuiPaper,
  Checkbox,
  TextField
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Favorite as FavoriteIcon,
  LocalHospital as HospitalIcon,
  Science as ScienceIcon,
  History as HistoryIcon,
  Timeline as TimelineIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Cake as CakeIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Badge as BadgeIcon,
  MedicalServices as MedicalServicesIcon,
  Vaccines as VaccinesIcon,
  MonitorHeart as MonitorHeartIcon,
  Psychology as PsychologyIcon,
  DirectionsRun as DirectionsRunIcon,
  Bedtime as BedtimeIcon,
  RemoveCircle as RemoveCircleIcon,
  PictureAsPdf as PictureAsPdfIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Business as BusinessIcon,
  Groups as GroupsIcon,
  Church as ChurchIcon,
  Height as HeightIcon,
  MonitorWeight as MonitorWeightIcon,
  Hotel as HotelIcon,
  Notes as NotesIcon,
  MenuBook as MenuBookIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  ContentCopy as ContentCopyIcon,
  Restaurant as RestaurantIcon,
  Lightbulb as LightbulbIcon,
  VideoFile as VideoFileIcon,
  AudioFile as AudioFileIcon,
  Archive as ArchiveIcon,
  Code as CodeIcon,
  TableChart as TableChartIcon,
  ReceiptLong as ReceiptLongIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL, FILE_BASE_URL } from '../config';
import { getFileUrl } from '../utils/urlHelper';
import RecibosPaciente from './RecibosPaciente';

// Función para obtener el icono según el tipo de archivo
const getFileIcon = (fileName: string) => {
  const extension = fileName.toLowerCase().split('.').pop();
  
  switch (extension) {
    case 'pdf':
      return <PictureAsPdfIcon sx={{ fontSize: 60, color: 'error.main' }} />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'svg':
    case 'webp':
      return <ImageIcon sx={{ fontSize: 60, color: 'primary.main' }} />;
    case 'doc':
    case 'docx':
      return <DescriptionIcon sx={{ fontSize: 60, color: 'info.main' }} />;
    case 'xls':
    case 'xlsx':
    case 'csv':
      return <TableChartIcon sx={{ fontSize: 60, color: 'success.main' }} />;
    case 'txt':
    case 'rtf':
      return <DescriptionIcon sx={{ fontSize: 60, color: 'text.secondary' }} />;
    case 'mp4':
    case 'avi':
    case 'mov':
    case 'wmv':
    case 'flv':
    case 'webm':
      return <VideoFileIcon sx={{ fontSize: 60, color: 'warning.main' }} />;
    case 'mp3':
    case 'wav':
    case 'flac':
    case 'aac':
      return <AudioFileIcon sx={{ fontSize: 60, color: 'secondary.main' }} />;
    case 'zip':
    case 'rar':
    case '7z':
    case 'tar':
    case 'gz':
      return <ArchiveIcon sx={{ fontSize: 60, color: 'warning.dark' }} />;
    case 'html':
    case 'css':
    case 'js':
    case 'json':
    case 'xml':
    case 'py':
    case 'java':
    case 'cpp':
    case 'c':
      return <CodeIcon sx={{ fontSize: 60, color: 'info.dark' }} />;
    default:
      return <InsertDriveFileIcon sx={{ fontSize: 60, color: 'grey.500' }} />;
  }
};



// Componentes estilizados
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: 'none',
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  '@media (max-width: 600px)': {
    padding: '8px',
    margin: '0',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden'
  }
}));

const ProfileBanner = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '100%',
  height: 200,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  borderRadius: '24px',
  marginBottom: theme.spacing(3),
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(3),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  '@media (max-width: 600px)': {
    height: '160px',
    padding: '16px',
    marginBottom: '12px',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
    backdropFilter: 'blur(4px)'
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
    pointerEvents: 'none'
  }
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 150,
  height: 150,
  border: `4px solid ${theme.palette.background.paper}`,
  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
  position: 'relative',
  transition: 'all 0.3s ease-in-out',
  '@media (max-width: 600px)': {
    width: '80px',
    height: '80px',
    border: '2px solid white',
  },
  '&:hover': {
    transform: 'scale(1.05)',
    cursor: 'pointer',
    boxShadow: '0 12px 40px rgba(0,0,0,0.2)'
  }
}));

const BannerContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  color: 'white',
  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
  width: '100%',
  maxWidth: '100%',
  '@media (max-width: 600px)': {
    gap: '12px',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '100%',
    textAlign: 'center',
    '& .MuiTypography-h4': {
      fontSize: '1.1rem !important',
      marginBottom: '8px !important',
      textAlign: 'center !important',
    },
    '& .MuiTypography-body1': {
      fontSize: '0.75rem !important',
    }
  }
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.spacing(2),
  background: '#f5f7fa',
  boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '@media (max-width: 600px)': {
    borderRadius: '8px',
    margin: '4px',
    '& .MuiCardContent-root': {
      padding: '8px',
    }
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
  }
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  transition: 'transform 0.2s ease-in-out',
  '@media (max-width: 600px)': {
    margin: '2px',
    height: '24px',
    '& .MuiChip-label': {
      fontSize: '0.7rem !important',
      padding: '0 8px',
    }
  },
  '&:hover': {
    transform: 'scale(1.05)'
  }
}));

const TimelineItem = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingLeft: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  '@media (max-width: 600px)': {
    paddingLeft: '16px',
    paddingBottom: '8px',
    '& .MuiTypography-body1': {
      fontSize: '0.8rem !important',
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 2,
    background: theme.palette.primary.main,
    opacity: 0.3
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: -4,
    top: 0,
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: theme.palette.primary.main
  }
}));

const SeguimientoItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  background: 'linear-gradient(90deg, #f5f7fa 0%, #c3cfe2 100%)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  position: 'relative',
  minHeight: 80
}));

const MedicamentoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  transition: 'all 0.3s ease',
  borderLeft: '4px solid transparent',
  '&.activo': {
    borderLeftColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  '&.inactivo': {
    borderLeftColor: theme.palette.grey[400],
    backgroundColor: alpha(theme.palette.grey[400], 0.05),
  },
  '&.vacuna': {
    borderLeftColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  }
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1, sm: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface AnalisisClinico {
  nombre: string;
  fecha: string;
  nombre_archivo: string; // El backend devuelve el nombre del archivo
  ruta_archivo: string; // El backend devuelve la ruta completa generada dinámicamente
}

const DetallesPaciente: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [paciente, setPaciente] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState(0);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [analisis, setAnalisis] = useState<AnalisisClinico[]>([]);
  const [openAnalisis, setOpenAnalisis] = useState(false);
  const [analisisActual, setAnalisisActual] = useState<AnalisisClinico | null>(null);
  const [selectedAnalisis, setSelectedAnalisis] = useState<AnalisisClinico | null>(null);
  const [isGeneratingResumen, setIsGeneratingResumen] = useState(false);
  const [resumenClinico, setResumenClinico] = useState<string>('');
  const [openDeleteResumenDialog, setOpenDeleteResumenDialog] = useState(false);
  const [modelosIA, setModelosIA] = useState<string[]>([]);
  const [modeloIA, setModeloIA] = useState<string>('');
  const [selectedAnalisisForResumen, setSelectedAnalisisForResumen] = useState<AnalisisClinico[]>([]);
  const [instruccionesAdicionales, setInstruccionesAdicionales] = useState<string>('');

  // Agregar estilo global para prevenir desbordamiento horizontal
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media (max-width: 600px) {
        body {
          overflow-x: hidden !important;
          max-width: 100vw !important;
        }
        * {
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Hacer scroll hacia arriba cuando el componente se monte
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchPaciente = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/pacientes/${id}`);
        setPaciente(response.data);
        
        // Cargar análisis clínicos y filtrar los eliminados
        const analisisResponse = await axios.get(`${API_BASE_URL}/pacientes/${id}/analisis`);
        const analisisFiltrados = analisisResponse.data.filter((analisis: AnalisisClinico) => 
          !analisis.nombre.includes('(ELIMINADO)')
        );
        setAnalisis(analisisFiltrados);
        
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar el paciente:', error);
        setLoading(false);
      }
    };

    const fetchModelosIA = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/modelos-ia`);
        setModelosIA(response.data.modelos);
        if (response.data.modelos.length > 0) {
          setModeloIA(response.data.modelos[0]);
        }
      } catch (error) {
        console.error('Error al cargar los modelos de IA:', error);
      }
    };

    fetchPaciente();
    fetchModelosIA();
  }, [id]);

  useEffect(() => {
    // Este efecto se ejecuta cada vez que el objeto 'paciente' se actualiza.
    // Si el paciente tiene un resumen guardado, lo ponemos en el estado 'resumenClinico' para mostrarlo.
    if (paciente && paciente.analisis_resumen) {
      setResumenClinico(paciente.analisis_resumen);
    }
  }, [paciente]); // <-- La dependencia clave es [paciente]

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/pacientes/${paciente.id}`);
      navigate('/pacientes');
    } catch (error) {
      console.error('Error al eliminar el paciente:', error);
    }
  };

  const handleGenerarResumen = async () => {
    if (selectedAnalisisForResumen.length === 0) {
      alert('Por favor seleccione al menos un análisis clínico');
      return;
    }

    setIsGeneratingResumen(true);
    setResumenClinico('');
    try {
      // Transformar los análisis para enviar nombre_archivo en lugar de ruta_archivo
      const analisisParaResumen = selectedAnalisisForResumen.map(analisis => ({
        nombre: analisis.nombre,
        fecha: analisis.fecha,
        nombre_archivo: analisis.nombre_archivo // Usar nombre_archivo en lugar de ruta_archivo
      }));

      // Paso A: Generar el resumen (esto ya funciona)
      const response = await axios.post(`${API_BASE_URL}/generar-resumen`, 
      {
        analisis: analisisParaResumen, // Enviar la versión transformada
        modelo: modeloIA,
        paciente: paciente,
        instrucciones_adicionales: instruccionesAdicionales
      }, 
      {
        timeout: 120000 
      });

      const resumenGenerado = response.data.resumen;
      setResumenClinico(resumenGenerado);

      // Paso B: Guardar el paciente actualizado (Lógica de persistencia)
      if (resumenGenerado) {
        const pacienteActualizado = { ...paciente };
        pacienteActualizado.analisis_resumen = resumenGenerado;

        // --- CAMBIO CLAVE Y ÚNICO ---
        // Llamamos al endpoint PUT usando el nombre del paciente, como tu API lo espera.
        await axios.put(`${API_BASE_URL}/pacientes/${paciente.id}`, pacienteActualizado);
        
        // Actualizamos el estado local para que la UI esté sincronizada
        setPaciente(pacienteActualizado);

        // console.log("Resumen generado y guardado exitosamente en el paciente.");
      }

    } catch (error: any) {
      let errorMsg = 'Error al generar o guardar el resumen.';
      if (axios.isCancel(error)) {
        errorMsg = 'La solicitud fue cancelada.';
      } else if (error.code === 'ECONNABORTED') {
        errorMsg = 'La conexión al servidor ha excedido el tiempo de espera. Por favor, intente de nuevo.';
      } else if (error.response) {
        errorMsg = error.response.data?.detail || errorMsg;
      }
      console.error('Error en el proceso de generación/guardado:', error);
      alert(errorMsg);
    } finally {
      setIsGeneratingResumen(false);
    }
  };

  const handleCopyToClipboard = () => {
    if (resumenClinico) {
      navigator.clipboard.writeText(resumenClinico)
        .then(() => {
          // Opcional: mostrar una notificación de éxito
          // console.log('Resumen copiado al portapapeles');
        })
        .catch((err) => {
          console.error('Error al copiar al portapapeles:', err);
        });
    }
  };

  const handleBorrarResumen = () => {
    setOpenDeleteResumenDialog(true);
  };

  const handleDeleteResumen = async () => {
    try {
      const pacienteActualizado = { ...paciente, analisis_resumen: '' };
      await axios.put(`${API_BASE_URL}/pacientes/${paciente.id}`, pacienteActualizado);
      setResumenClinico('');
      setPaciente(pacienteActualizado);
      // console.log('Resumen clínico borrado');
    } catch (error) {
      console.error('Error al borrar el resumen:', error);
    }
    setOpenDeleteResumenDialog(false);
  };

  // DEBUG: Mostrar el objeto paciente en consola
  // console.log('DEBUG PACIENTE', paciente);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Cargando datos del paciente...
        </Typography>
      </Box>
    );
  }

  if (!paciente) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Paciente no encontrado</Typography>
    </Box>
  );
  }

  return (
    <Box sx={{ 
      maxWidth: { xs: '100%', sm: '600px', md: '900px', lg: '1200px', xl: '1536px' }, 
      mx: 'auto', 
      px: { xs: 1, sm: 2, md: 3 }, 
      py: 4 
    }}>
      <StyledPaper elevation={0}>
        <ProfileBanner>
          <BannerContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <ProfileAvatar
                src={paciente?.ruta_imagen_perfil ? getFileUrl(paciente.ruta_imagen_perfil) : undefined}
                alt={paciente.nombre}
                onClick={() => paciente.ruta_imagen_perfil && setOpenImageDialog(true)}
                sx={{
                  cursor: paciente.ruta_imagen_perfil ? 'pointer' : 'default',
                  '&:hover': {
                    transform: paciente.ruta_imagen_perfil ? 'scale(1.05)' : 'none'
                  }
                }}
              >
                {!paciente?.ruta_imagen_perfil && (
                  paciente.nombre ? 
                    paciente.nombre.split(' ').map((word: string) => word.charAt(0)).join('').toUpperCase().slice(0, 2) : 
                    '?'
                )}
              </ProfileAvatar>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', sm: 'flex-start' } }}>
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 1, textAlign: { xs: 'center', sm: 'left' }, fontSize: { xs: '1.3rem', sm: '2.125rem' } }}>
                  {paciente.nombre}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 1, sm: 2 }, 
                  flexDirection: { xs: 'row', sm: 'row' }, 
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                  alignItems: 'center'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CakeIcon sx={{ color: 'white', transform: 'translateY(-2px)', fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                    <Typography variant="body1" sx={{ color: 'white', fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                      {paciente.edad > 0 ? `${paciente.edad} años` : 'Edad no registrada'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EmailIcon sx={{ color: '#fff', transform: 'translateY(-2px)', fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                    <Typography variant="body1" sx={{ color: 'white', fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                      {paciente.email}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BadgeIcon sx={{ color: 'white', transform: 'translateY(-2px)', fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                    <Typography variant="body1" sx={{ color: 'white', fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                      DNI: {paciente.dni}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </BannerContent>
        </ProfileBanner>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: { xs: 'center', sm: 'flex-end' }, 
          mb: 3, 
          gap: 2, 
          flexDirection: { xs: 'row', sm: 'row' }, // Cambiado para mobile
          mt: 2
        }}>
          <Tooltip title="Ver emails del paciente" placement="top">
            <Button
              variant="contained"
              color="info"
              onClick={() => {
                localStorage.setItem('selectedPacienteForEmail', JSON.stringify(paciente));
                navigate('/email');
              }}
              sx={{
                borderRadius: '50%',
                minWidth: '56px',
                width: '56px',
                height: '56px',
                boxShadow: '0 4px 14px 0 rgba(3, 169, 244, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px 0 rgba(3, 169, 244, 0.4)',
                }
              }}
            >
              <EmailIcon sx={{ fontSize: { xs: '1.2rem', sm: '2rem' } }} />
            </Button>
          </Tooltip>
          <Tooltip title="Editar paciente" placement="top">
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/pacientes/agregar?id=${id}&tab=${value}`)}
              sx={{
                borderRadius: '50%',
                minWidth: '56px',
                width: '56px',
                height: '56px',
                boxShadow: '0 4px 14px 0 rgba(25, 118, 210, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px 0 rgba(25, 118, 210, 0.4)',
                }
              }}
            >
              <EditIcon sx={{ fontSize: { xs: '1.2rem', sm: '2rem' } }} />
            </Button>
          </Tooltip>
          <Tooltip title="Eliminar paciente" placement="top">
            <Button
              variant="contained"
              color="error"
              onClick={() => setOpenDeleteDialog(true)}
              sx={{
                borderRadius: '50%',
                minWidth: '56px',
                width: '56px',
                height: '56px',
                boxShadow: '0 4px 14px 0 rgba(244, 67, 54, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px 0 rgba(244, 67, 54, 0.4)',
                }
              }}
            >
              <DeleteIcon sx={{ fontSize: { xs: '1.2rem', sm: '2rem' } }} />
            </Button>
          </Tooltip>
        </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={value} 
              onChange={handleChange} 
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  minWidth: { xs: 60, sm: 100 },
                  padding: { xs: '8px 12px', sm: '16px 20px' },
                  color: theme.palette.text.secondary,
                  transition: 'all 0.2s ease-in-out',
                  borderRadius: '8px 8px 0 0',
                  margin: '0 4px',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    color: theme.palette.primary.main,
                  },
                  '&.Mui-selected': {
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                  '& .MuiTouchRipple-root': {
                    borderRadius: '8px 8px 0 0',
                  }
                },
                '& .MuiTabs-scrollButtons': {
                  color: theme.palette.primary.main,
                  '&.Mui-disabled': {
                    opacity: 0.3
                  }
                }
              }}
            >
              <Tab 
                label="Datos Personales" 
                icon={<PersonIcon sx={{ mb: 0.5 }} />} 
                iconPosition="start"
              />
              <Tab 
                label="Preferencias Alimentarias" 
                icon={<RestaurantIcon sx={{ mb: 0.5 }} />} 
                iconPosition="start"
              />
              <Tab 
                label="Medicamentos y Vacunas" 
                icon={<MedicalServicesIcon sx={{ mb: 0.5 }} />} 
                iconPosition="start"
              />
              <Tab 
                label="Análisis Clínicos" 
                icon={<ScienceIcon sx={{ mb: 0.5 }} />} 
                iconPosition="start"
              />
              <Tab 
                label="Historial Clínico" 
                icon={<HistoryIcon sx={{ mb: 0.5 }} />} 
                iconPosition="start"
              />
              <Tab 
                label="Seguimiento" 
                icon={<TimelineIcon sx={{ mb: 0.5 }} />} 
                iconPosition="start"
              />
              <Tab 
                label="Recibos" 
                icon={<ReceiptLongIcon sx={{ mb: 0.5 }} />} 
                iconPosition="start"
                disabled
              />
            </Tabs>
          </Box>

        <Box sx={{ mt: 3 }}>
          <CustomTabPanel value={value} index={0}>
            <Grid container spacing={3} sx={{ justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Información Personal
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <PersonIcon sx={{ color: paciente.nombre ? 'primary.main' : 'grey.500' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Nombre Completo" 
                          secondary={paciente.nombre} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <BadgeIcon sx={{ color: paciente.dni ? 'primary.main' : 'grey.500' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="DNI" 
                          secondary={paciente.dni} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CakeIcon sx={{ color: paciente.fecha_nacimiento ? 'primary.main' : 'grey.500' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Fecha de Nacimiento y Edad" 
                          secondary={
                            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Typography component="span" variant="body2">{paciente.fecha_nacimiento}</Typography>
                              {paciente.edad > 0 && (
                                <Typography component="span" variant="body2">{paciente.edad} años</Typography>
                              )}
                            </Box>
                          } 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <EmailIcon sx={{ color: paciente.email ? 'primary.main' : 'grey.500' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Correo Electrónico" 
                          secondary={
                            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Typography component="span" variant="body2">{paciente.email}</Typography>
                              {paciente.email2 && (
                                <Typography component="span" variant="body2">{paciente.email2}</Typography>
                              )}
                            </Box>
                          } 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ChurchIcon sx={{ color: paciente.religion ? 'primary.main' : 'grey.500' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Religión" 
                          secondary={paciente.religion} 
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
                </Grid>

              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Información Social y Laboral
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <BusinessIcon sx={{ color: paciente.obra_social ? 'primary.main' : 'grey.500' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Obra Social" 
                          secondary={
                            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Typography component="span" variant="body2">{paciente.obra_social}</Typography>
                              <Typography component="span" variant="body2">Plan: {paciente.plan}</Typography>
                              <Typography component="span" variant="body2">Nº Socio: {paciente.nro_socio}</Typography>
                            </Box>
                          } 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <WorkIcon sx={{ color: paciente.profesion ? 'primary.main' : 'grey.500' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Profesión" 
                          secondary={paciente.profesion} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <GroupsIcon sx={{ color: paciente.familia ? 'primary.main' : 'grey.500' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Familia" 
                          secondary={paciente.familia} 
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
                </Grid>

              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Medidas Corporales
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <HeightIcon sx={{ color: paciente.altura_cm && paciente.altura_cm > 0 ? 'primary.main' : 'grey.500' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Altura" 
                          secondary={
                            paciente.altura_cm && paciente.altura_cm > 0
                              ? `${paciente.altura_cm} cm`
                              : <Typography component="span" variant="body2" color="text.secondary">Altura no registrada</Typography>
                          } 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <MonitorWeightIcon sx={{ color: paciente.peso_kg && paciente.peso_kg > 0 ? 'primary.main' : 'grey.500' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Peso Actual" 
                          secondary={(function() {
                            // Buscar el peso más reciente
                            let pesoActual = null;
                            if (paciente.pesos && paciente.pesos.length > 0) {
                              const pesosOrdenados = [...paciente.pesos].sort((a, b) => {
                                const [dA, mA, yA] = a[0].split('/');
                                const [dB, mB, yB] = b[0].split('/');
                                const dateA = new Date((yA.length === 2 ? 2000 + Number(yA) : Number(yA)), Number(mA) - 1, Number(dA));
                                const dateB = new Date((yB.length === 2 ? 2000 + Number(yB) : Number(yB)), Number(mB) - 1, Number(dB));
                                return dateB.getTime() - dateA.getTime();
                              });
                              const [fecha, valor] = pesosOrdenados[0];
                              // Calcular diferencia de días
                              const [d, m, y] = fecha.split('/');
                              const fechaPeso = new Date((y.length === 2 ? 2000 + Number(y) : Number(y)), Number(m) - 1, Number(d));
                              const hoy = new Date();
                              const diffTime = Math.abs(hoy.setHours(0,0,0,0) - fechaPeso.setHours(0,0,0,0));
                              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                              if (diffDays <= 7) {
                                pesoActual = `${Number(valor).toFixed(3)} kg`;
                              }
                            }
                            return pesoActual 
                              ? pesoActual 
                              : <Typography component="span" variant="body2" color="text.secondary">Peso desactualizado</Typography>;
                          })()} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <TimelineIcon sx={{ color: paciente.pesos && paciente.pesos.length > 0 ? 'primary.main' : 'grey.500' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Historial de Pesos" 
                          secondary={
                            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {paciente.pesos && paciente.pesos.length > 0 ? (
                                [...paciente.pesos]
                                  .sort((a, b) => {
                                    // a[0] y b[0] son las fechas en formato DD/MM/YY
                                    const parse = (f: string) => {
                                      const [d, m, y] = f.split('/');
                                      // Si el año es de 2 dígitos, lo convierto a 20YY
                                      const year = y.length === 2 ? 2000 + Number(y) : Number(y);
                                      const month = Number(m) - 1;
                                      const day = Number(d);
                                      return new Date(year, month, day);
                                    };
                                    return parse(b[0]).getTime() - parse(a[0]).getTime();
                                  })
                                  .map((peso: [string, string], index: number) => (
                                    <Typography key={index} component="span" variant="body2">
                                      {peso[0]}: {Number(peso[1]).toFixed(3)} kg
                                    </Typography>
                                  ))
                              ) : (
                                <Typography component="span" variant="body2" color="text.secondary">
                                  No hay registros de peso
                                </Typography>
                              )}
                            </Box>
                          } 
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
                </Grid>
              </Grid>
          </CustomTabPanel>

          <CustomTabPanel value={value} index={1}>
              <Grid container spacing={3} sx={{ justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Preferencias Alimentarias
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {paciente.preferencias && paciente.preferencias.length > 0 ? (
                        paciente.preferencias.map((pref: string, index: number) => (
                          <StyledChip
                            key={index}
                            label={pref}
                            color="primary"
                            variant="outlined"
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No hay preferencias alimentarias registradas
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </StyledCard>
                </Grid>

              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Restricciones Alimentarias
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {paciente.intolerancias && paciente.intolerancias.length > 0 ? (
                        paciente.intolerancias.map((rest: string, index: number) => (
                          <StyledChip
                            key={index}
                            label={rest}
                            color="error"
                            variant="outlined"
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No hay restricciones alimentarias registradas
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </StyledCard>
                </Grid>

                <Grid item xs={12}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Observaciones Alimentarias
                    </Typography>
                    {paciente.observaciones_alimentacion ? (
                      <Typography variant="body1">
                        {paciente.observaciones_alimentacion}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No hay observaciones alimentarias registradas
                      </Typography>
                    )}
                  </CardContent>
                </StyledCard>
                </Grid>
            </Grid>
          </CustomTabPanel>

          <CustomTabPanel value={value} index={2}>
            <Grid container spacing={3} sx={{ justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Medicamentos
                    </Typography>
                    {paciente.medicamentos?.length > 0 ? (
                      <Box>
                        {/* Medicamentos Activos */}
                        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, color: 'success.main' }}>
                          Medicamentos Activos
                        </Typography>
                        {paciente.medicamentos
                          .filter((med: string[]) => {
                            if (!med[0]) return false;
                            
                            // Si es permanente, siempre está activo
                            if (med[4] === 'true') {
                              return true;
                            }
                            
                            // Si no es permanente, verificar por fecha y duración
                            if (!med[3]) return false;
                            
                            try {
                              // Convertir fecha DD/MM/YY a YYYY-MM-DD
                              const [dia, mes, anio] = med[0].split('/');
                              const anioCompleto = parseInt(anio) < 50 ? `20${anio}` : `19${anio}`;
                              const fechaInicio = new Date(`${anioCompleto}-${mes}-${dia}`);
                              const duracion = parseInt(med[3]) || 0;
                              const fechaFin = new Date(fechaInicio);
                              fechaFin.setDate(fechaFin.getDate() + duracion);
                              const hoy = new Date();
                              return fechaInicio <= hoy && hoy <= fechaFin;
                            } catch (error) {
                              console.error('Error procesando fecha:', error);
                              return false;
                            }
                          })
                          .map((med: string[], index: number) => (
                            <MedicamentoCard key={`activo-${index}`} className="activo">
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                  {med[1]}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Fecha de inicio: {med[0]}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Frecuencia: {med[2]}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {med[4] === 'true' ? 'Duración: Permanente' : `Duración: ${med[3]} días`}
                                </Typography>
                              </Box>
                            </MedicamentoCard>
                          ))}

                        {/* Medicamentos Inactivos */}
                        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, color: 'text.secondary' }}>
                          Historial de Medicamentos
                        </Typography>
                        {paciente.medicamentos
                          .filter((med: string[]) => {
                            if (!med[0]) return false;
                            
                            // Los medicamentos permanentes nunca están inactivos
                            if (med[4] === 'true') {
                              return false;
                            }
                            
                            // Si no es permanente, verificar por fecha y duración
                            if (!med[3]) return false;
                            
                            try {
                              // Convertir fecha DD/MM/YY a YYYY-MM-DD
                              const [dia, mes, anio] = med[0].split('/');
                              const anioCompleto = parseInt(anio) < 50 ? `20${anio}` : `19${anio}`;
                              const fechaInicio = new Date(`${anioCompleto}-${mes}-${dia}`);
                              const duracion = parseInt(med[3]) || 0;
                              const fechaFin = new Date(fechaInicio);
                              fechaFin.setDate(fechaFin.getDate() + duracion);
                              const hoy = new Date();
                              return fechaFin <= hoy;
                            } catch (error) {
                              console.error('Error procesando fecha:', error);
                              return false;
                            }
                          })
                          .map((med: string[], index: number) => (
                            <MedicamentoCard key={`inactivo-${index}`} className="inactivo">
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                  {med[1]}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Fecha de inicio: {med[0]}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Frecuencia: {med[2]}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Duración: {med[3]} días
                                </Typography>
                              </Box>
                            </MedicamentoCard>
                          ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No hay medicamentos registrados
                      </Typography>
                    )}
                  </CardContent>
                </StyledCard>
                </Grid>

              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Vacunas
                    </Typography>
                    {paciente.vacunas?.length > 0 ? (
                      <Box>
                        {paciente.vacunas.map((vacuna: string[], index: number) => (
                          <MedicamentoCard key={`vacuna-${index}`} className="vacuna">
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                {vacuna[1]}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Fecha: {vacuna[0]}
                              </Typography>
                            </Box>
                          </MedicamentoCard>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No hay vacunas registradas
                      </Typography>
                    )}
                  </CardContent>
                </StyledCard>
                </Grid>
            </Grid>
          </CustomTabPanel>

          <CustomTabPanel value={value} index={3}>
            <Grid container spacing={3} sx={{ justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                <Grid item xs={12}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Análisis Clínicos
                    </Typography>
                    {analisis && analisis.length > 0 ? (
                      <>
                        <Grid container spacing={2}>
                          {analisis.map((analisisItem: AnalisisClinico, index: number) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <StyledCard sx={{ position: 'relative', height: 'auto' }}>
                                <Checkbox
                                    checked={selectedAnalisisForResumen.some(a => a.nombre === analisisItem.nombre)}
                                    onChange={() => {
                                      setSelectedAnalisisForResumen(prev => {
                                        const exists = prev.some(a => a.nombre === analisisItem.nombre);
                                        if (exists) {
                                          return prev.filter(a => a.nombre !== analisisItem.nombre);
                                        } else {
                                          return [...prev, analisisItem];
                                        }
                                      });
                                    }}
                                    sx={{
                                      position: 'absolute',
                                      top: 4,
                                      left: 4,
                                      zIndex: 2,
                                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                      '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 1)',
                                      }
                                    }}
                                  />
                                <Box
                                  sx={{
                                    height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.200',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => setSelectedAnalisis(analisisItem)}
                                >
                                  {getFileIcon(analisisItem.nombre_archivo)}
                                </Box>
                                <CardContent>
                                  <Tooltip title={analisisItem.nombre}>
                                      <Typography variant="subtitle1" noWrap>
                                          {analisisItem.nombre}
                                      </Typography>
                                  </Tooltip>
                                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                    <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => setSelectedAnalisis(analisisItem)}>
                                      Ver
                                    </Button>
                                    <Button size="small" variant="outlined" color="primary" startIcon={<DownloadIcon />} onClick={() => window.open(getFileUrl(analisisItem.ruta_archivo), '_blank')}>
                                      Descargar
                                    </Button>
                                  </Box>
                                </CardContent>
                              </StyledCard>
                            </Grid>
                          ))}
                        </Grid>

                        {/* --- SECCIÓN NUEVA PARA GENERAR RESUMEN --- */}
                        <Box sx={{ mt: 4, p: 3, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LightbulbIcon color="primary" sx={{ transform: 'translateY(-1px)' }} />
                            Generar Resumen con IA
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Seleccione los análisis clínicos, elija un modelo de IA y genere un resumen detallado.
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                            <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 150 }, flexGrow: 0 }}>
                              <InputLabel>Modelo de IA</InputLabel>
                              <Select
                                value={modeloIA}
                                onChange={(e) => setModeloIA(e.target.value)}
                                label="Modelo de IA"
                              >
                                {modelosIA.map((modelo) => (
                                  <MenuItem key={modelo} value={modelo}>
                                    {modelo.split('/').pop()}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handleGenerarResumen}
                              disabled={isGeneratingResumen || selectedAnalisisForResumen.length === 0}
                              startIcon={isGeneratingResumen ? <CircularProgress size={20} color="inherit" /> : <LightbulbIcon />}
                              sx={{
                                '& .MuiButton-startIcon': {
                                  display: 'flex',
                                  alignItems: 'center',
                                  marginRight: 1,
                                  transform: 'translateY(-1px)',
                                }
                              }}
                            >
                              {isGeneratingResumen ? 'Generando...' : `Generar Resumen (${selectedAnalisisForResumen.length})`}
                            </Button>
                            
                            {/* --- BLOQUE NUEVO AÑADIDO --- */}
                            {resumenClinico && (
                              <Button
                                variant="outlined"
                                onClick={handleCopyToClipboard}
                                startIcon={<ContentCopyIcon />}
                                sx={{
                                  color: 'grey.700',
                                  borderColor: 'grey.400',
                                  '&:hover': {
                                    backgroundColor: 'grey.100',
                                    borderColor: 'grey.600',
                                  },
                                }}
                              >
                                Copiar Resumen
                              </Button>
                            )}
                            {/* --- FIN DEL BLOQUE NUEVO --- */}
                          </Box>

                          {/* Campo de instrucciones adicionales */}
                          <Box sx={{ mb: 2 }}>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              variant="outlined"
                              label="Instrucciones Adicionales (opcional)"
                              placeholder="Ej: Enfócate en los valores de colesterol y triglicéridos. Incluye recomendaciones específicas de dieta mediterránea. Menciona si hay riesgo cardiovascular."
                              value={instruccionesAdicionales}
                              onChange={(e) => setInstruccionesAdicionales(e.target.value)}
                              helperText="Agrega instrucciones específicas para personalizar el resumen según tus necesidades"
                              sx={{ 
                                '& .MuiOutlinedInput-root': {
                                  '&:hover fieldset': {
                                    borderColor: 'primary.main',
                                  },
                                },
                              }}
                            />
                          </Box>

                          {resumenClinico && (
                            <Paper elevation={0} sx={{ p: 2, mt: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200', borderRadius: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">
                                  Resumen Clínico Generado
                                </Typography>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={handleBorrarResumen}
                                  sx={{
                                    opacity: 0.7,
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      opacity: 1,
                                      backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                      transform: 'scale(1.1)'
                                    }
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                                {resumenClinico}
                              </Typography>
                            </Paper>
                          )}
                        </Box>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No hay análisis clínicos registrados
                      </Typography>
                    )}
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          </CustomTabPanel>

          <CustomTabPanel value={value} index={4}>
            <Grid container spacing={3} sx={{ justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              {/* COLUMNA 1 */}
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Aparato Circulatorio
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          {paciente.calambres ? (
                            <ErrorIcon color="error" />
                          ) : (
                            <RemoveCircleIcon color="disabled" />
                          )}
                        </ListItemIcon>
                        <ListItemText primary="Calambres" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          {paciente.varices ? (
                            <ErrorIcon color="error" />
                          ) : (
                            <RemoveCircleIcon color="disabled" />
                          )}
                        </ListItemIcon>
                        <ListItemText primary="Varices" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <MenuBookIcon sx={{ color: paciente.observaciones_circulatorio ? 'primary.main' : 'grey.500' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Observaciones Circulatorio"
                          secondary={
                            paciente.observaciones_circulatorio
                              ? paciente.observaciones_circulatorio
                              : <Typography component="span" variant="body2" color="text.secondary">No registrado</Typography>
                          }
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>

              {/* COLUMNA 2 */}
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Aparato Respiratorio
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          {paciente.alergias ? (
                            <ErrorIcon color="error" />
                          ) : (
                            <RemoveCircleIcon color="disabled" />
                          )}
                        </ListItemIcon>
                        <ListItemText primary="Alergias" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          {paciente.fuma_fumo ? (
                            <ErrorIcon color="error" />
                          ) : (
                            <RemoveCircleIcon color="disabled" />
                          )}
                        </ListItemIcon>
                        <ListItemText primary="Fuma/Fumó" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <MenuBookIcon sx={{ color: paciente.observaciones_respiratorio ? 'primary.main' : 'grey.500' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Observaciones Respiratorio"
                          secondary={
                            paciente.observaciones_respiratorio
                              ? paciente.observaciones_respiratorio
                              : <Typography component="span" variant="body2" color="text.secondary">No registrado</Typography>
                          }
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>

              {/* COLUMNA 1 */}
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Aparato Digestivo
                    </Typography>
                    <List>
                      <ListItem><ListItemIcon>{paciente.gastritis ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Gastritis" /></ListItem>
                      <ListItem><ListItemIcon>{paciente.gases_intestinales ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Gases Intestinales" /></ListItem>
                      <ListItem><ListItemIcon>{paciente.dolor_abdominal ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Dolor Abdominal" /></ListItem>
                      <ListItem><ListItemIcon>{paciente.hepatitis ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Hepatitis" /></ListItem>
                      <ListItem>
                        <ListItemIcon><AccessTimeIcon sx={{ color: paciente.frecuencia_intestino ? 'primary.main' : 'grey.500' }} /></ListItemIcon>
                        <ListItemText primary="Frecuencia Intestinal" secondary={paciente.frecuencia_intestino || "No registrado"}/>
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><MenuBookIcon sx={{ color: paciente.observaciones_digestivo ? 'primary.main' : 'grey.500' }} /></ListItemIcon>
                        <ListItemText primary="Observaciones Digestivo" secondary={paciente.observaciones_digestivo || "No registrado"}/>
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>

              {/* COLUMNA 1 */}
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Aparato Urinario
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          {paciente.infecciones_urinarias ? (
                            <ErrorIcon color="error" />
                          ) : (
                            <RemoveCircleIcon color="disabled" />
                          )}
                        </ListItemIcon>
                        <ListItemText primary="Infecciones Urinarias" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          {paciente.colicos_litiasis ? (
                            <ErrorIcon color="error" />
                          ) : (
                            <RemoveCircleIcon color="disabled" />
                          )}
                        </ListItemIcon>
                        <ListItemText primary="Cólicos/Litiasis" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <MenuBookIcon sx={{ color: paciente.observaciones_urinario ? 'primary.main' : 'grey.500' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Observaciones Urinario"
                          secondary={
                            paciente.observaciones_urinario
                              ? paciente.observaciones_urinario
                              : <Typography component="span" variant="body2" color="text.secondary">No registrado</Typography>
                          }
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>

              {/* COLUMNA 2 */}
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Sistema Endocrino
                    </Typography>
                    <List>
                      <ListItem><ListItemIcon>{paciente.enfermedad_tiroides ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Enfermedad Tiroides" /></ListItem>
                      <ListItem><ListItemIcon>{paciente.uso_corticoides ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Uso de Corticoides" /></ListItem>
                      <ListItem><ListItemIcon>{paciente.diabetes ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Diabetes" /></ListItem>
                      <ListItem><ListItemIcon>{paciente.colesterol_elevado ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Colesterol Elevado" /></ListItem>
                      <ListItem><ListItemIcon>{paciente.trigliceridos_elevado ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Triglicéridos Elevados" /></ListItem>
                      <ListItem>
                        <ListItemIcon><MenuBookIcon sx={{ color: paciente.observaciones_endocrino ? 'primary.main' : 'grey.500' }} /></ListItemIcon>
                        <ListItemText primary="Observaciones Endocrino" secondary={paciente.observaciones_endocrino || "No registrado"}/>
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>
              
              {/* ---- AQUÍ SE AGREGAN LAS TARJETAS CORREGIDAS ---- */}

              {/* COLUMNA 1 */}
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Dermatológico
                    </Typography>
                    <List>
                      <ListItem><ListItemIcon>{paciente.manchas ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Manchas" /></ListItem>
                      <ListItem><ListItemIcon>{paciente.piel_seca ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Piel Seca" /></ListItem>
                      <ListItem><ListItemIcon>{paciente.piel_grasa ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Piel Grasa" /></ListItem>
                      <ListItem><ListItemIcon>{paciente.urticaria_eczema ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Urticaria/Eczema" /></ListItem>
                      <ListItem><ListItemIcon>{paciente.caida_cabello ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Caída de Cabello" /></ListItem>
                      <ListItem><ListItemIcon>{paciente.fragilidad_unas ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Fragilidad de Uñas" /></ListItem>
                      <ListItem>
                        <ListItemIcon><MenuBookIcon sx={{ color: paciente.observaciones_dermatologico ? 'primary.main' : 'grey.500' }} /></ListItemIcon>
                        <ListItemText primary="Observaciones Dermatológico" secondary={paciente.observaciones_dermatologico || "No registrado"}/>
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Locomotor
                    </Typography>
                    <List>
                      <ListItem><ListItemIcon>{paciente.zona_cervical ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Zona Cervical" /></ListItem>
                      <ListItem><ListItemIcon>{paciente.zona_dorsal ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Zona Dorsal" /></ListItem>
                      <ListItem><ListItemIcon>{paciente.zona_lumbar ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="Zona Lumbar" /></ListItem>
                      <ListItem><ListItemIcon>{paciente.mmii ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="MMII" /></ListItem>
                      <ListItem><ListItemIcon>{paciente.mmss ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon><ListItemText primary="MMSS" /></ListItem>
                      <ListItem>
                        <ListItemIcon><MenuBookIcon sx={{ color: paciente.observaciones_locomotor ? 'primary.main' : 'grey.500' }} /></ListItemIcon>
                        <ListItemText primary="Observaciones Locomotor" secondary={paciente.observaciones_locomotor || "No registrado"} />
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>

              {/* COLUMNA 2 */}
              

              {/* TARJETA DE ANCHO COMPLETO AL FINAL */}
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Hábitos y Sueño
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon><DirectionsRunIcon sx={{ color: paciente.actividad_fisica ? 'primary.main' : 'grey.500' }} /></ListItemIcon>
                        <ListItemText primary="Actividad Física" secondary={paciente.actividad_fisica || "No registrado"}/>
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          {paciente.psicoanalisis ? (
                            <ErrorIcon color="error" />
                          ) : (
                            <RemoveCircleIcon color="disabled" />
                          )}
                        </ListItemIcon>
                        <ListItemText primary="Psicoanálisis" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><AccessTimeIcon sx={{ color: paciente.duerme_de && paciente.duerme_hasta ? 'primary.main' : 'grey.500' }} /></ListItemIcon>
                        <ListItemText primary="Horario de Sueño" secondary={paciente.duerme_de && paciente.duerme_hasta ? `${paciente.duerme_de} - ${paciente.duerme_hasta}` : "No registrado"}/>
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><HotelIcon sx={{ color: paciente.despertar_noche ? 'primary.main' : 'grey.500' }} /></ListItemIcon>
                        <ListItemText primary="Despertar Nocturno" secondary={paciente.despertar_noche || "No registrado"}/>
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Antecedentes Quirúrgicos e Infecciosos
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon><MedicalServicesIcon sx={{ color: paciente.antecedentes_quirurgicos ? 'primary.main' : 'grey.500' }} /></ListItemIcon>
                        <ListItemText primary="Antecedentes Quirúrgicos" secondary={paciente.antecedentes_quirurgicos || "No registrado"}/>
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>{paciente.covid ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon>
                        <ListItemText primary="COVID" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><MenuBookIcon sx={{ color: paciente.otras_infecciosas ? 'primary.main' : 'grey.500' }} /></ListItemIcon>
                        <ListItemText primary="Otras Infecciosas" secondary={paciente.otras_infecciosas || "No registrado"}/>
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Antecedentes Ginecológicos
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon><AccessTimeIcon sx={{ color: paciente.fum ? 'primary.main' : 'grey.500' }} /></ListItemIcon>
                        <ListItemText
                          primary="FUM"
                          secondary={Array.isArray(paciente.fum) ? paciente.fum.join('\n') : (paciente.fum || "No registrado")}
                          secondaryTypographyProps={{ style: { whiteSpace: 'pre-line' } }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><AccessTimeIcon sx={{ color: paciente.embarazos ? 'primary.main' : 'grey.500' }} /></ListItemIcon>
                        <ListItemText primary="Embarazos" secondary={paciente.embarazos || "No registrado"}/>
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><AccessTimeIcon sx={{ color: paciente.fecha_ultimo_control ? 'primary.main' : 'grey.500' }} /></ListItemIcon>
                        <ListItemText primary="Fecha Último Control" secondary={paciente.fecha_ultimo_control || "No registrado"}/>
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>{paciente.climaterio ? <ErrorIcon color="error" /> : <RemoveCircleIcon color="disabled" />}</ListItemIcon>
                        <ListItemText primary="Climaterio" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><MenuBookIcon sx={{ color: paciente.observaciones_ginecologico ? 'primary.main' : 'grey.500' }} /></ListItemIcon>
                        <ListItemText primary="Observaciones Ginecológico" secondary={paciente.observaciones_ginecologico || "No registrado"}/>
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          </CustomTabPanel>

          <CustomTabPanel value={value} index={5}>
              <Grid container spacing={3} sx={{ justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                <Grid item xs={12}>
                <StyledCard>
                      <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Seguimiento
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      {paciente.seguimiento && paciente.seguimiento.length > 0 ? (
                        paciente.seguimiento.map((seg: any, index: number) => (
                          <SeguimientoItem key={index}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                {seg.fecha}
                              </Typography>
                              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                {seg.texto}
                              </Typography>
                            </Box>
                          </SeguimientoItem>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                          No hay registros de seguimiento
                        </Typography>
                      )}
                    </Box>
                      </CardContent>
                </StyledCard>
                  </Grid>
              </Grid>
          </CustomTabPanel>

          <CustomTabPanel value={value} index={6}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              py: 8,
              textAlign: 'center'
            }}>
              <ReceiptLongIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
              <Typography variant="h5" color="text.secondary" gutterBottom>
                Función Deshabilitada
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
                La funcionalidad de recibos está temporalmente deshabilitada.
              </Typography>
            </Box>
          </CustomTabPanel>
        </Box>
      </StyledPaper>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro que desea eliminar al paciente {paciente.nombre}? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
                              </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openImageDialog}
        onClose={() => setOpenImageDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Foto de Perfil
          <IconButton
            onClick={() => setOpenImageDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                width: 300,
                height: 300,
                borderRadius: '50%',
                overflow: 'hidden',
                boxShadow: 3,
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={paciente?.ruta_imagen_perfil ? getFileUrl(paciente.ruta_imagen_perfil) : undefined}
                alt={paciente.nombre}
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

                <Dialog 
                  open={openAnalisis} 
                  onClose={() => setOpenAnalisis(false)} 
                  maxWidth="xl" 
                  fullWidth
                  PaperProps={{
                    sx: { mt: 4, maxHeight: '90vh' }
                  }}
                >
                  <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {analisisActual?.nombre}
                    <Button onClick={() => setOpenAnalisis(false)} color="error" variant="outlined" size="small">
                      Cerrar
                    </Button>
                  </DialogTitle>
                  <DialogContent sx={{ minHeight: 700, p: 0 }}>
                    {analisisActual && analisisActual.ruta_archivo && analisisActual.nombre.match(/\.(pdf)$/i) ? (
                      <iframe
                        src={getFileUrl(analisisActual.ruta_archivo)}
                        title="Análisis PDF"
                        width="100%"
                        height="700px"
                        style={{ border: 'none' }}
                      />
                    ) : analisisActual && analisisActual.ruta_archivo && analisisActual.nombre.match(/\.(jpg|jpeg|png|bmp|tiff|webp)$/i) ? (
                      <img
                        src={getFileUrl(analisisActual.ruta_archivo)}
                        alt={analisisActual.nombre}
                        style={{ maxWidth: '100%', maxHeight: 700, display: 'block', margin: '0 auto', objectFit: 'contain' }}
                      />
                    ) : (
                      <Typography sx={{ p: 2 }}>No se puede mostrar este tipo de archivo.</Typography>
                    )}
                  </DialogContent>
                </Dialog>

      <Dialog
        open={!!selectedAnalisis}
        onClose={() => setSelectedAnalisis(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          {selectedAnalisis?.nombre}
          <IconButton
            onClick={() => setSelectedAnalisis(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedAnalisis && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2,
              alignItems: 'center',
              height: '100%',
              width: '100%'
            }}>
              {selectedAnalisis && selectedAnalisis.ruta_archivo && selectedAnalisis.ruta_archivo.toLowerCase().match(/\.(pdf)$/) ? (
                <iframe
                  src={getFileUrl(selectedAnalisis.ruta_archivo)}
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    border: 'none',
                    flex: 1
                  }}
                  title="PDF Viewer"
                />
              ) : selectedAnalisis && selectedAnalisis.ruta_archivo && selectedAnalisis.ruta_archivo.toLowerCase().match(/\.(jpg|jpeg|png|bmp|tiff|webp)$/) ? (
                <img
                  src={getFileUrl(selectedAnalisis.ruta_archivo)}
                  alt={selectedAnalisis.nombre}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              ) : null}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDeleteResumenDialog}
        onClose={() => setOpenDeleteResumenDialog(false)}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro que desea borrar el resumen clínico? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteResumenDialog(false)}>Cancelar</Button>
          <Button onClick={handleDeleteResumen} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DetallesPaciente; 